import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  fetchCategories,
  fetchMenuItems,
  setSelectedCatId,
} from "./slices/menuSlice";
import { fetchItemDetails } from "./slices/itemSlice";
import { useSettings } from "./context/SettingsContext";
import MenuModal from "./components/MenuModal";
import MenuItemList from "./components/MenuItemList";
import CartBar from "./components/CartBar";
import CartModal from "./components/CartModal";
import ServiceSelectionModal from "./components/ServiceSelectionModal";
import CartIcon from "./components/CartIcon";
import { toast } from "react-toastify";
import axios from "axios"; // ✅ add this
import "./style.css";
import { fetchComboDetails } from './slices/comboSlice';
import ComboModal from './components/ComboModal';
import RadialCategoryMenu from "./components/RadialCategoryMenu";
import { useSearchParams } from "react-router-dom";
import { decodeId } from "./utils/encode";
import { useRef } from "react";



const Menu = () => {
  const dispatch = useDispatch();
  const validateOnceRef = useRef(false);
  const menuStartRef = useRef(null);

  const { settings, loading: settingsLoading } = useSettings();

  const { categories, selectedCatId, menuItems, loading, error } = useSelector(
    (state) => state.menu
  );
  const [selectedSubFilter, setSelectedSubFilter] = useState("all");
const [showRadialMenu, setShowRadialMenu] = useState(false);

  const cartItemToEdit = useSelector((state) => state.cart.itemToEdit);
  const user = useSelector((state) => state.auth.user);
const [categoryLoading, setCategoryLoading] = useState(false);
const [showCategoryDrawer, setShowCategoryDrawer] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [cartRefreshKey, setCartRefreshKey] = useState(0);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [serviceData, setServiceData] = useState(null);
  const [pendingMenuId, setPendingMenuId] = useState(null);
  const [hasShownEligibleToast, setHasShownEligibleToast] = useState(false);
  const [hasShownAlertToast, setHasShownAlertToast] = useState(false);
  const [subtotal, setSubtotal] = useState(0);
const [comboModalOpen, setComboModalOpen] = useState(false);
const mobileCategoryView = Number(settings?.mobile_category_view ?? 2);
const [searchParams, setSearchParams] = useSearchParams();


useEffect(() => {
  const encodedMenu = searchParams.get("menu");
  if (!encodedMenu) return;
  if (validateOnceRef.current) return;

  const menuId = decodeId(encodedMenu);
  if (!menuId) {
    toast.error("Invalid menu link");
    validateOnceRef.current = true;
    return;
  }

  validateOnceRef.current = true;

  axios
    .post(`${window.BASE_API_URL}validatemenu`, {
      menu_id: menuId,
    })
    .then((res) => {
      if (res.data?.success === true) {
        const categoryId = String(res.data.category_id); // ✅ normalize

        // ⏳ WAIT until categories are loaded
        const waitForCategories = setInterval(() => {
          if (categories?.length) {
            dispatch(setSelectedCatId(categoryId));
            clearInterval(waitForCategories);
          }
        }, 50);

        dispatch(fetchItemDetails(menuId));
        setModalOpen(true);

        searchParams.delete("menu");
        setSearchParams(searchParams, { replace: true });
      } else {
        toast.error(res.data?.message || "Menu not available");
      }
    })
    .catch(() => toast.error("Unable to validate menu"));
}, [dispatch, searchParams, setSearchParams, categories]);



const changeCategory = (catId) => {
  if (catId === selectedCatId) return;

  if (menuStartRef.current) {
    const y =
      menuStartRef.current.getBoundingClientRect().top +
      window.pageYOffset-70;

    window.scrollTo({
      top: y,
      behavior: "auto", // change to "smooth" if you want
    });
  }

  dispatch(setSelectedCatId(catId));
};


  // ✅ new state for user order count
  const [orderCount, setOrderCount] = useState(null);

  // ✅ Fetch user order count when user is logged in
  useEffect(() => {
    const fetchUserOrderCount = async () => {
      if (!user?.id) return;

      try {
        const res = await axios.get(
          `${window.BASE_API_URL}getUserOrderCount/${user.id}`
        );
        if (res.data) {
          setOrderCount(res.data);
        } else {
          setOrderCount(0);
        }
      } catch (error) {
        console.error("Error fetching order count:", error);
        setOrderCount(0);
      }
    };

    fetchUserOrderCount();
  }, [user]);

  // ✅ subtotal tracking
  useEffect(() => {
    const cartData = JSON.parse(localStorage.getItem("cart")) || [];

    const freeMenuIds = Array.isArray(settings?.free_menu_id)
      ? settings.free_menu_id.map(String)
      : [];

    const subtotalWithoutFree = cartData.reduce((sum, item) => {
      const isFree =
        freeMenuIds.includes(String(item.itemId)) ||
        String(item.itemId)?.startsWith("free_");
      return isFree ? sum : sum + (parseFloat(item.total) || 0);
    }, 0);

    setSubtotal(subtotalWithoutFree);
  }, [cartRefreshKey, modalOpen, cartModalOpen, settings]);

  // ✅ Toast logic for free item offer
  useEffect(() => {
    if (!settings?.certain_offer_status || !settings?.freeitem_amount) return;
    if( ! (settings?.user_welcome_status != 1 || settings?.both_offer == 1))
    {
      return ;
    }
    const freeItemAmount = Number(settings.freeitem_amount);
    const alertGap = Number(settings.freeitem_alert || 0);

    if (subtotal >= freeItemAmount && !hasShownEligibleToast) {
      toast.success("🎉 You are now eligible for a offer item!");
      setHasShownEligibleToast(true);
      setHasShownAlertToast(false);
    } else if (
      subtotal >= freeItemAmount - alertGap &&
      subtotal < freeItemAmount &&
      !hasShownAlertToast &&
      !hasShownEligibleToast
    ) {
      const diff = (freeItemAmount - subtotal).toFixed(2);
      toast.info(`🛒 Add ₹${diff} more to unlock your offer item!`);
      setHasShownAlertToast(true);
    } else if (subtotal < freeItemAmount - alertGap) {
      setHasShownEligibleToast(false);
      setHasShownAlertToast(false);
    }
  }, [subtotal, settings, hasShownEligibleToast, hasShownAlertToast]);

  // Detect mobile vs desktop
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  
useEffect(() => {
  const handleOpenComboEditor = () => {
    const comboItem = JSON.parse(localStorage.getItem("editComboItem"));
    if (comboItem && comboItem.comboId) {
      // dispatch(fetchComboDetails(comboItem.comboId));
      setComboModalOpen(true);
    }
  };

  window.addEventListener("openComboEditor", handleOpenComboEditor);
  return () => window.removeEventListener("openComboEditor", handleOpenComboEditor);
}, [dispatch]);

  useEffect(() => {
    const savedService = localStorage.getItem("orderService");
    if (savedService) setServiceData(JSON.parse(savedService));
  }, []);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
  if (!categories || categories.length === 0) return;

  const urlCatId = searchParams.get("id");
  if (!urlCatId) return;

  // check if category exists
  const exists = categories.some(
    (cat) => String(cat.id) === String(urlCatId)
  );

  if (exists) {
    dispatch(setSelectedCatId(urlCatId));
  }

  // remove ?id from URL (clean URL)
  searchParams.delete("id");
  setSearchParams(searchParams, { replace: true });
}, [categories, searchParams, setSearchParams, dispatch]);


  useEffect(() => {
    if (selectedCatId) dispatch(fetchMenuItems(selectedCatId));
  }, [dispatch, selectedCatId]);

  useEffect(() => {
    if (cartItemToEdit) setModalOpen(true);
  }, [cartItemToEdit]);
useEffect(() => {
  if (menuItems && menuItems.menucat) {
    const timer = setTimeout(() => setCategoryLoading(false), 300); // smooth transition
    return () => clearTimeout(timer);
  }
}, [menuItems]);

  const isEligibleForFreeItem =
  settings?.certain_offer_status == "1" &&
  subtotal >= Number(settings?.freeitem_amount || 0);

const isEligibleForFirstOrderOffer =
  user &&
  settings?.user_welcome_status == "1" &&
  settings?.welcome_offer_type == "1" &&
  orderCount === 0;
  useEffect(() => {
  if (!categories?.length || !settings?.freeitem_id) return;

  // If user not eligible and currently on Free Item or First Order Offer
  const onFreeItem =
    String(selectedCatId) === `free_${settings.freeitem_id}`;

  if (
    (!isEligibleForFreeItem && onFreeItem)
  ) {
    const firstCategory = categories[0];
    if (firstCategory?.id) {
      dispatch(setSelectedCatId(firstCategory.id));
    }
  }
}, [
  isEligibleForFreeItem,
  selectedCatId,
  categories,
  settings?.freeitem_id,
  dispatch,
]);


// ✅ Refresh full category list but keep current selection
const refreshCategories = async () => {
  // if (!selectedCatId) return;
  // try {
  //   // setCategoryLoading(true);

  //   // Re-fetch category list
  //   await dispatch(fetchCategories());

  //   // ✅ Re-fetch menu items of current category
  //   await dispatch(fetchMenuItems(selectedCatId));
  // } catch (err) {
  //   console.error("Error refreshing categories:", err);
  // } finally {
  //   // setTimeout(() => setCategoryLoading(false), 300); // small delay for smoothness
  // }
};
useEffect(() => {
  const handleOpenItemModal = () => {
    setModalOpen(true);
  };
  window.addEventListener("openItemModal", handleOpenItemModal);
  return () => window.removeEventListener("openItemModal", handleOpenItemModal);
}, []);

const handleViewClick = (menuId) => {
  const dineinSession = JSON.parse(localStorage.getItem("dinein"));
  
  if (dineinSession && dineinSession.tableName) {

    if (selectedCatId === 'combo') {
      dispatch(fetchComboDetails(menuId));
      setComboModalOpen(true);
      return;
    }
    // ✅ Dine-in active, skip service modal
    dispatch(fetchItemDetails(menuId));
    setModalOpen(true);
    return;
  }

  if (!serviceData && settings?.is_choose_service == 1) {
    setPendingMenuId(menuId);
    setServiceModalOpen(true);
    return;
  }


   if (selectedCatId === 'combo') {
      dispatch(fetchComboDetails(menuId));
      setComboModalOpen(true);
      return;
    }

  dispatch(fetchItemDetails(menuId));
  setModalOpen(true);
};


  const handleServiceConfirm = (data) => {
    setServiceData(data);
    localStorage.setItem("orderService", JSON.stringify(data));
    setServiceModalOpen(false);

    if (pendingMenuId) {

       if (selectedCatId === 'combo') {
      dispatch(fetchComboDetails(pendingMenuId));
      setComboModalOpen(true);
      return;
    }
    // ✅ Dine-in active, skip service modal
    dispatch(fetchItemDetails(pendingMenuId));
    setModalOpen(true);
    return;


      // dispatch(fetchItemDetails(pendingMenuId));
      // setModalOpen(true);
      // setPendingMenuId(null);
    }
  };


if (loading) {
  const foodEmojis = ["🍕", "🍔", "🍟", "🍣", "🍩", "🥗", "🌮", "🍰", "🍦", "🍜"];
  const randomEmoji = foodEmojis[Math.floor(Math.random() * foodEmojis.length)];

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-b from-orange-50 to-white">
      <div className="relative w-24 h-24 mb-6">
        <div className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        <div className="absolute inset-2 flex items-center justify-center text-5xl animate-pulse">
          {randomEmoji}
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-700 animate-bounce">
        Cooking up something delicious...
      </h2>

      <p className="text-sm text-gray-500 mt-3 animate-pulse">
        Please wait while we prepare your menu 🍽️
      </p>
    </div>
  );
}


  if (error) return <p className="error-text">Error: {error}</p>;

  // ✅ Build categories dynamically
  const extendedCategories = [...categories];
// Subcategories list for filtering
const subcategories = menuItems?.activesubcategory || [];


  const hasAnySubcategoryItems = subcategories?.some(
  (sub) => sub.menusubcat && sub.menusubcat.length > 0
);


const dineinSession = JSON.parse(localStorage.getItem("dinein"));
const isDineInActive = dineinSession && dineinSession.tableName;

  // console.log(subtotal, settings?.certain_offer_status, settings?.freeitem_amount);
  // Free item
  if (
    !isDineInActive && 
    settings?.certain_offer_status == "1" &&
    subtotal >= Number(settings?.freeitem_amount || 0) 
  ) {
    extendedCategories.push({
      id: "free_" + settings.freeitem_id,
      name: "Free Item",
      isFree: true,
    });
  }

  // ✅ Show First Order Offer only if user has 0 orders
  if (
    user &&
    settings?.user_welcome_status == "1" &&
    settings?.welcome_offer_type == "1" &&
    orderCount === 0
  ) {
    extendedCategories.push({
      id: "first_offer",
      name: "First Order Offer",
      isFree: true,
    });
  }

// ❌ Stop everything if dine-in is disabled
if (
  settings?.is_dinein == 0 && 
  JSON.parse(localStorage.getItem("dinein"))?.tableName
) {
  return (
    <div className="flex flex-col items-center p-6 h-screen bg-red-50">
      <div className="text-center p-6 bg-white shadow-md rounded-lg max-w-md">
        <h2 className="text-xl font-bold text-red-600 mb-2">
          Dine-In Not Available
        </h2>
        <p className="text-gray-700">
          Restaurant is not taking dine-in orders, please try again.
        </p>
      </div>
    </div>
  );
}

const renderMenuContent = () => (
  <>
      <div ref={menuStartRef} />
    {/* 🔽 Subcategory Filter Bar */}
    {!categoryLoading && subcategories.length > 0 && hasAnySubcategoryItems && (
      <div className="subcat-filter-bar">
        <button
          className={`subcat-filter-btn ${
            selectedSubFilter === "all" ? "active" : ""
          }`}
          onClick={() => setSelectedSubFilter("all")}
        >
          All
        </button>

        {subcategories.map((sub) =>
          sub.menusubcat.length > 0 ? (
            <button
              key={sub.id}
              className={`subcat-filter-btn ${
                selectedSubFilter === sub.id ? "active" : ""
              }`}
              onClick={() => setSelectedSubFilter(sub.id)}
            >
              {sub.name}
            </button>
          ) : null
        )}
      </div>
    )}

    {/* Loader */}
    {categoryLoading && (
      <div className="flex justify-center py-16">
        <span className="text-gray-500">Loading menu...</span>
      </div>
    )}

    {/* Subcategories */}
    {!categoryLoading &&
      (
        selectedSubFilter === "all"
          ? menuItems?.activesubcategory
          : menuItems?.activesubcategory?.filter(
              (s) => s.id === selectedSubFilter
            )
      )?.map(
        (sub) =>
          sub.menusubcat.length > 0 && (
            <section key={sub.id} className="subcategory-section">
              <h2 className="subcat-title">{sub.name}</h2>
              <MenuItemList
                items={sub.menusubcat}
                selectedCatId={selectedCatId}
                onView={handleViewClick}
                onRefreshCategories={refreshCategories}
              />
            </section>
          )
      )}

    {/* All Items */}
    {!categoryLoading &&
      selectedSubFilter === "all" &&
      menuItems?.menucat?.length > 0 && (
        <section className="subcategory-section">
          <h2 className="subcat-title">All Items</h2>
          <MenuItemList
            items={menuItems.menucat}
            selectedCatId={selectedCatId}
            onView={handleViewClick}
            onRefreshCategories={refreshCategories}
          />
        </section>
      )}
  </>
);

const renderMobileDropdownCategory = () => (
  <div className="sticky top-0 z-30 bg-white border-b p-3">
    <select
      value={selectedCatId}
      onChange={(e) => changeCategory(e.target.value)}
      className="w-full border rounded-lg px-3 py-2 text-sm"
      style={{ border: "2px solid "+settings?.cart_btn_color || "#111827"}}
    >
      {extendedCategories.map((cat) => (
        <option key={cat.id} value={cat.id}>
          {cat.name}
        </option>
      ))}
    </select>
  </div>
);
const renderMobileHorizontalCategory = () => (
  <div className="sticky-nav">
          <nav className="category-nav z-50">
            {extendedCategories.map((cat) => (
              <button
                key={cat.id}
                className={`category-btn ${
                  cat.id == selectedCatId ? "active" : ""
                }`}
                onClick={() => {
                  if (cat.id === selectedCatId) return;
                  changeCategory(cat.id);
                }}
              >
                {cat.name}
              </button>
            ))}
          </nav>
        </div>
);

 return (
  <div className={`menu-container ${settings?.menu_view == 0 ? "list-layout" : ""}`}>

    {/* ================= GRID VIEW (UNCHANGED) ================= */}
    {settings?.menu_view == 1 && (
      <>
        <div className="sticky-nav">
          <nav className="category-nav z-50">
            {extendedCategories.map((cat) => (
              <button
              style={{ color: settings?.cart_title_color || "#111827", borderBottom: `${cat.id == selectedCatId ? '4px solid '+settings?.cart_btn_color : ''}` }}
                key={cat.id}
                className={`category-btn ${
                  cat.id == selectedCatId ? "active" : ""
                }`}
                onClick={() => {
                  if (cat.id === selectedCatId) return;
                  changeCategory(cat.id);
                }}
              >
                {cat.name}
              </button>
            ))}
          </nav>
        </div>

        {renderMenuContent()}
      </>
    )}

    {/* ================= LIST VIEW (NEW) ================= */}
{settings?.menu_view == 0 && !isMobile && (
  <div className="flex h-screen overflow-hidden">

    {/* LEFT CATEGORY – scrollable */}
    <aside className="w-64 bg-white border-r overflow-y-auto">
      <h3 className="px-4 py-4 font-bold sticky top-0 bg-white">
        Categories
      </h3>

      {extendedCategories.map((cat) => (
        <button
          key={cat.id}
          
           style={{ background: `${
            selectedCatId == cat.id
              ?  settings?.cart_btn_color
              : "white"
          }` || "#111827" }}
          onClick={() => changeCategory(cat.id)}
          className={`w-full text-left px-4 py-3 ${
            selectedCatId == cat.id
              ? "bg-black text-white"
              : "hover:bg-gray-100"
          }`}
        >
          {cat.name}
        </button>
      ))}
    </aside>

    {/* RIGHT MENU – scrollable */}
    <main className="flex-1 overflow-y-auto bg-gray-50">
      {renderMenuContent()}
    </main>
  </div>
)}
{settings?.menu_view == 0 && isMobile && (
  <div className="relative h-screen overflow-y-auto bg-gray-50">

    {/* MOBILE CATEGORY UI */}
    {mobileCategoryView === 0 && renderMobileDropdownCategory()}

    {mobileCategoryView === 1 && renderMobileHorizontalCategory()}

    {/* MENU CONTENT */}
    {renderMenuContent()}

    {/* RADIAL CATEGORY */}
    {mobileCategoryView === 2 && (
      <>
        <button
          onClick={() => setShowRadialMenu(true)}
          className="fixed left-3 top-1/2 -translate-y-1/2 z-40
                     w-12 h-12 rounded-full bg-black text-white
                     shadow-xl flex items-center justify-center
                     active:scale-95 transition"
        >
          ☰
        </button>

        <RadialCategoryMenu
          open={showRadialMenu}
          categories={extendedCategories}
          selectedCatId={selectedCatId}
          onSelect={(id) => {
            changeCategory(id);
            setShowRadialMenu(false);
          }}
          onToggle={() => setShowRadialMenu(false)}
        />
      </>
    )}
  </div>
)}







    {/* ================= CART / MODALS (UNCHANGED) ================= */}

    {isMobile ? (
      <CartBar key={cartRefreshKey} onCartClick={() => setCartModalOpen(true)} />
    ) : (
      <CartIcon key={cartRefreshKey} onClick={() => setCartModalOpen(true)} />
    )}

    {(modalOpen || cartItemToEdit) && (
      <MenuModal
        onClose={() => {
          setModalOpen(false);
          dispatch({ type: "cart/clearItemToEdit" });
          setCartRefreshKey((prev) => prev + 1);
        }}
        isEditing={!!cartItemToEdit}
      />
    )}

    {cartModalOpen && (
      <CartModal
        openMenuModal={() => setModalOpen(true)}
        onClose={() => {
          setCartModalOpen(false);
          setCartRefreshKey((prev) => prev + 1);
        }}
        isEligibleForFreeItem={isEligibleForFreeItem}
        isEligibleForFirstOrderOffer={isEligibleForFirstOrderOffer}
      />
    )}

    {comboModalOpen && (
      <ComboModal
        onClose={() => {
          setComboModalOpen(false);
          setCartRefreshKey((prev) => prev + 1);
        }}
      />
    )}

    {serviceModalOpen && (
      <ServiceSelectionModal
        onConfirm={handleServiceConfirm}
        onClose={() => setServiceModalOpen(false)}
      />
    )}
  </div>
);

};

export default Menu;
